/**
 * The rows that did not go in, as a file the person can take away.
 *
 * Built here, from what the apply already returned, rather than fetched. That
 * is not an optimisation: the last time this was a link at an endpoint, the
 * browser navigated to it without the bearer token the interceptor puts on
 * XHRs, the API answered 401, and the person got the browser's own error page
 * over an export that worked perfectly. A file assembled from data already in
 * hand has no request to lose a token on.
 *
 * Rows are gathered by reason, because that is how a person acts on them: one
 * class is fixed one way.
 */
import {
  PlanRow,
  TransferSchema,
} from '../../services/table-import-export.service';
import { groupByReason } from './plan-decisions';
import { reasonCopy } from './reason-copy';

/**
 * Reason first, then our own words for it, then the row as it was read. The
 * field columns come from the schema, so the file has the entity's own shape
 * without this knowing which entity that is.
 */
export function notImportedCsv(
  rows: readonly PlanRow[],
  schema: TransferSchema,
): string {
  const fields = schema.fields;
  const header = ['Row', 'Reason', 'What happened', ...fields.map(f => f.label)];
  const lines = [csvLine(header)];

  for (const group of groupByReason(rows)) {
    const copy = reasonCopy(group.reason);
    for (const row of group.rows) {
      lines.push(csvLine([
        String(row.rowNo),
        group.reason,
        // The row's own detail when there is one - it names the column or the
        // record - and the class sentence when there is not.
        row.detail ?? copy.sentence,
        ...fields.map(field => row.values[field.name] ?? ''),
      ]));
    }
  }

  return lines.join('\r\n') + '\r\n';
}

/** Named after the file it came from, so it is obvious which run it belongs to. */
export function notImportedFilename(sourceFileName: string): string {
  const stem = sourceFileName.replace(/\.[^.]+$/, '').trim();
  const safe = stem.replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '');
  return `${safe || 'import'}-not-imported.csv`;
}

/**
 * Excel reads a leading BOM as "this is UTF-8". Without it a name with an
 * accent in it comes back mangled, which on a list of rows to correct is the
 * kind of damage that gets typed back into the database by hand.
 */
export function notImportedBlob(csv: string): Blob {
  return new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
}

function csvLine(values: readonly string[]): string {
  return values.map(csvCell).join(',');
}

function csvCell(value: string): string {
  // A cell opening with =, +, - or @ is run as a formula by spreadsheet
  // software. These cells hold whatever was in someone's file, so they are not
  // ours to trust with that.
  const guarded = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}
